import { useGetZendeskMessagingJwt } from "#resources/hooks/integrations/zendesk/query";

export interface ZendeskConversationField {
	id: string;
	value: string;
}

export const ZENDESK_FIELD_IDS = {
	userName: "49128817541147",
	requesterName: "50077802337435",
	requesterEmail: "50077842762267",
	dashPermissions: "50072083680283",
	orgLogin: "50077770100251",
	organizationId: "49128501829531",
	organizationName: "48757383658651",
	placeId: "49128731886619",
	placeName: "49128711570587",
	eventId: "49128623254939",
	eventName: "49128614519707",
	source: "49128446589979",
	isKeyAccount: "49785057294491",
	placeSize: "49785105216283",
	placeType: "49785751098267",
} as const;

export type ZendeskIsKeyAccountValue = "sim_cliente" | "não_cliente";

export type ZendeskPlaceSizeValue =
	| "pp_tamanho_casa"
	| "p_tamanho_casa"
	| "m_tamanho_casa"
	| "g_tamanho_casa"
	| "mega_tamanho_casa"
	| "não_identificado_tamanho_casa";

export type ZendeskPlaceTypeValue =
	| "sim_casa_evento"
	| "não_casa_evento"
	| "não_se_aplica_evento";

const ZENDESK_WIDGET_KEY = process.env.ZENDESK_WIDGET_KEY;

let loadPromise: Promise<void> | null = null;

let openIntent = false;

// Launcher sempre visível em home/place; nas demais telas só após mensagem
// (messageTriggered) ou abertura pelo botão (openIntent). Só a navegação esconde.
const ALWAYS_VISIBLE_PATHS = ["^\\/$", "^\\/place\\/[^\\/]+$"];

let lastPath = "";
let alwaysVisibleByPath = false;
let messageTriggered = false;

let unreadCount = 0;
const unreadListeners = new Set<(count: number) => void>();

function setUnreadCount(count: number) {
	unreadCount = count;
	unreadListeners.forEach(listener => listener(count));
}

export function subscribeZendeskUnread(listener: (count: number) => void) {
	unreadListeners.add(listener);
	listener(unreadCount);
	return () => {
		unreadListeners.delete(listener);
	};
}

// Notificados a cada abertura do chat. Evento transitório, sem replay na inscrição.
const openListeners = new Set<() => void>();

export function subscribeZendeskOpen(listener: () => void) {
	openListeners.add(listener);
	return () => {
		openListeners.delete(listener);
	};
}

function isAlwaysVisiblePath(pathname: string) {
	return ALWAYS_VISIBLE_PATHS.some(path => new RegExp(path).test(pathname));
}

function shouldShowLauncher() {
	return alwaysVisibleByPath || messageTriggered || openIntent;
}

function applyLauncherVisibility() {
	const zE = getZE();
	if (!zE) return;
	if (shouldShowLauncher()) {
		zE("messenger", "show");
	} else {
		// Ao esconder, fecha também a janela do chat caso esteja aberta.
		zE("messenger", "close");
		zE("messenger", "hide");
	}
}

function registerLoginUser(zE: Function) {
	zE("messenger", "loginUser", async (callback: (token: string) => void) => {
		try {
			const token = await useGetZendeskMessagingJwt();
			if (token) callback(token);
		} catch {}
	});
}

function getZE(): Function | undefined {
	return ((window as unknown) as Record<string, Function>).zE;
}

function loadZendeskScript(): Promise<void> {
	if (!ZENDESK_WIDGET_KEY) return Promise.resolve();
	if (window.Zendesk) return Promise.resolve();
	if (loadPromise) return loadPromise;

	loadPromise = new Promise((resolve, reject) => {
		const settings = document.createElement("script");
		settings.textContent = `
			window.zESettings = {
				messenger: { launcher: { visible: false } }
			};
		`;
		document.head.appendChild(settings);

		const script = document.createElement("script");
		script.id = "ze-snippet";
		script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_WIDGET_KEY}`;
		script.defer = true;

		script.onload = () => {
			const zE = getZE()!;

			zE("messenger:set", "cookies", true);
			zE("messenger", "hide");
			zE("messenger:on", "unreadMessages", (count: number) => {
				setUnreadCount(count);
				if (count > 0) {
					messageTriggered = true;
					applyLauncherVisibility();
				}
			});
			zE("messenger:on", "open", () => {
				openListeners.forEach(listener => listener());
			});
			registerLoginUser(zE);

			window.Zendesk = {
				open: () => zE("messenger", "open"),
				close: () => zE("messenger", "close"),
				set: (field, value) => zE("messenger:set", field, value),
			};
			resolve();
		};
		script.onerror = () => {
			loadPromise = null;
			reject(new Error("Zendesk script failed to load"));
		};
		document.head.appendChild(script);
	});

	return loadPromise;
}

export function setZendeskOpenIntent(value: boolean) {
	openIntent = value;
}

export async function openZendeskChat(fields?: ZendeskConversationField[]) {
	await loadZendeskScript();

	if (!openIntent) return;

	const zE = getZE();
	if (!zE) return;

	if (fields?.length) {
		zE("messenger:set", "conversationFields", fields);
	}
	zE("messenger", "show");
	zE("messenger", "open");
}

// Sincroniza o launcher com a rota atual. Ao trocar de rota, zera os gatilhos de
// visibilidade (mensagem/botão) — único momento em que a bolha volta a sumir.
export async function syncZendeskPath(
	pathname: string,
	fields?: ZendeskConversationField[],
) {
	if (pathname !== lastPath) {
		lastPath = pathname;
		messageTriggered = false;
		// Abandonar a tela encerra a sessão de suporte aberta pelo botão.
		openIntent = false;
	}
	alwaysVisibleByPath = isAlwaysVisiblePath(pathname);

	await loadZendeskScript();

	const zE = getZE();
	if (!zE) return;

	if (fields?.length) {
		zE("messenger:set", "conversationFields", fields);
	}

	applyLauncherVisibility();
}
